import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  LeaveApplication, 
  LeaveBalance,
  PaginationParams 
} from '@/types';
import { 
  useFetch, 
  usePaginatedFetch, 
} from './useApi';
import { apiClient } from '@/lib/api';

// Query keys
const LEAVE_APPLICATIONS_KEY = 'leave-applications';
const LEAVE_BALANCES_KEY = 'leave-balances';

// ==================== Leave Applications ====================

export function useLeaveApplications(params?: { status?: string; employeeId?: string } & PaginationParams) {
  return usePaginatedFetch<{ data: LeaveApplication[]; meta: { total: number } }>(
    LEAVE_APPLICATIONS_KEY,
    '/payroll/leave',
    {
      page: params?.page || 1,
      limit: params?.limit || 20,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    },
    {
      status: params?.status,
      employeeId: params?.employeeId,
    }
  );
}

export function useMyLeaveApplications(params?: { status?: string } & PaginationParams) {
  return usePaginatedFetch<{ data: LeaveApplication[]; meta: { total: number } }>(
    `${LEAVE_APPLICATIONS_KEY}-my`,
    '/payroll/leave/my',
    {
      page: params?.page || 1,
      limit: params?.limit || 20,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    },
    {
      status: params?.status,
    }
  );
}

export function useLeaveApplication(id: string) {
  return useFetch<LeaveApplication>(
    [LEAVE_APPLICATIONS_KEY, id],
    `/payroll/leave/${id}`,
    undefined,
    { enabled: !!id }
  );
}

export function useApplyForLeave() {
  const queryClient = useQueryClient();
  
  return useMutation<LeaveApplication, Error, {
    leaveType: string;
    startDate: string;
    endDate: string;
    hoursPerDay: number;
    reason?: string;
  }>({
    mutationFn: (data) => apiClient.post('/payroll/leave', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LEAVE_APPLICATIONS_KEY] });
    },
  });
}

export function useApproveLeave() {
  const queryClient = useQueryClient();
  
  return useMutation<LeaveApplication, Error, { 
    id: string; 
    approved: boolean;
    notes?: string;
  }>({
    mutationFn: ({ id, approved, notes }) => 
      apiClient.patch(`/payroll/leave/${id}/approve`, { approved, notes }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [LEAVE_APPLICATIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LEAVE_APPLICATIONS_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: [LEAVE_BALANCES_KEY] });
    },
  });
}

export function useCancelLeave() {
  const queryClient = useQueryClient();
  
  return useMutation<LeaveApplication, Error, string>({
    mutationFn: (id) => apiClient.patch(`/payroll/leave/${id}/cancel`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [LEAVE_APPLICATIONS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LEAVE_APPLICATIONS_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [LEAVE_BALANCES_KEY] });
    },
  });
}

// ==================== Leave Balances ====================

export function useLeaveBalances(employeeId?: string) {
  return useFetch<LeaveBalance[]>(
    [LEAVE_BALANCES_KEY, employeeId || 'current'],
    employeeId 
      ? `/payroll/employees/${employeeId}/leave-balances` 
      : '/payroll/leave/balances',
    undefined,
    { enabled: true }
  );
}

export function useUpdateLeaveBalance() {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, {
    employeeId: string;
    leaveType: string;
    entitlement?: number;
    adjustment?: number;
  }>({
    mutationFn: ({ employeeId, ...data }) => 
      apiClient.patch(`/payroll/employees/${employeeId}/leave-balances`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [LEAVE_BALANCES_KEY] });
      queryClient.invalidateQueries({ queryKey: [LEAVE_BALANCES_KEY, variables.employeeId] });
    },
  });
}
